// components/AddressDrawer.js
import React, { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faHome, faBriefcase, faMapMarkerAlt, faCrosshairs, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { GoogleMap, useLoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY } from "../helpers/Constant";

// Map container style
const containerStyle = {
  width: "100%",
  height: "100%",
};

// Default center (Ahmedabad, India)
const defaultCenter = {
  lat: 23.0276,
  lng: 72.5871,
};

const libraries = ["places"]; // for Autocomplete

const AddressDrawer = ({ isOpen, onClose, addressForm, onAddressChange, onSubmit, isSubmitting }) => {
  const [mapPosition, setMapPosition] = useState(defaultCenter);
  const [isLocating, setIsLocating] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [mapError, setMapError] = useState(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
    // Add a callback to handle script loading errors
    onLoad: () => {
      console.log("✅ Google Maps script loaded successfully");
    },
    onError: (error) => {
      console.error("❌ Google Maps script load error:", error);
      setMapError(error);
    },
  });

  // Log load state changes
  useEffect(() => {
    if (loadError) {
      console.error("useLoadScript error:", loadError);
      setMapError(loadError);
    }
  }, [loadError]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Reverse geocode using Google Geocoding API (fallback if needed)
  const fetchAddressFromCoords = async (lat, lng) => {
    setAddressLoading(true);
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();
      if (data.status === "OK" && data.results.length > 0) {
        const result = data.results[0];
        const fakeAddressEvent = { target: { name: "address", value: result.formatted_address } };
        onAddressChange(fakeAddressEvent);

        const addressComponents = result.address_components || [];
        let area = "";
        let apartment = "";
        for (const comp of addressComponents) {
          const types = comp.types;
          if (types.includes("street_number")) {
            apartment = comp.long_name;
          }
          if (types.includes("route") || types.includes("sublocality") || types.includes("locality")) {
            area = comp.long_name;
          }
        }
        if (apartment) {
          const aptEvent = { target: { name: "apartment", value: apartment } };
          onAddressChange(aptEvent);
        }
        if (area) {
          const areaEvent = { target: { name: "area", value: area } };
          onAddressChange(areaEvent);
        }
      } else {
        console.warn("Geocoding failed:", data.status);
      }
    } catch (error) {
      console.error("Geocoding error:", error);
    } finally {
      setAddressLoading(false);
    }
  };

  // Update map and address when a location is selected
  const handleLocationSelect = (lat, lng) => {
    setMapPosition({ lat, lng });
    const latEvent = { target: { name: "lat", value: lat.toString() } };
    const lngEvent = { target: { name: "long", value: lng.toString() } };
    onAddressChange(latEvent);
    onAddressChange(lngEvent);
    fetchAddressFromCoords(lat, lng);
  };

  // Map click handler
  const onMapClick = (e) => {
    const { lat, lng } = e.latLng.toJSON();
    handleLocationSelect(lat, lng);
  };

  // Marker drag end
  const onMarkerDragEnd = (e) => {
    const { lat, lng } = e.latLng.toJSON();
    handleLocationSelect(lat, lng);
  };

  // Get user's current location
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        handleLocationSelect(latitude, longitude);
        setIsLocating(false);
      },
      (error) => {
        console.error("Error getting location:", error);
        alert("Unable to get your location. Please allow location access and try again.");
        setIsLocating(false);
      },
      { enableHighAccuracy: true }
    );
  };

  // Handle Autocomplete place selection
  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const pos = { lat, lng };
        setMapPosition(pos);
        handleLocationSelect(lat, lng);
        if (place.formatted_address) {
          const fakeEvent = { target: { name: "address", value: place.formatted_address } };
          onAddressChange(fakeEvent);
        }
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(e);
  };

  if (!isOpen) return null;

  // Show a fallback UI if the map fails to load
  if (loadError || mapError) {
    const errorMsg = loadError?.message || mapError?.message || "Unknown error";
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
        <div className="text-center p-8 max-w-md">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <p className="text-red-500 text-lg">Error loading Google Maps</p>
          <p className="text-gray-700 text-sm mt-2">{errorMsg}</p>
          <p className="text-sm text-gray-500 mt-4">
            Please check your API key and ensure the Maps JavaScript API is enabled.
          </p>
          <div className="mt-6 flex gap-3 justify-center">
            <button
              onClick={() => {
                setMapError(null);
                window.location.reload(); // refresh the script
              }}
              className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
            >
              Retry
            </button>
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while script is loading
  if (!isLoaded) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-orange-500" />
          <p className="mt-2 text-gray-600">Loading map...</p>
        </div>
      </div>
    );
  }

  // Main drawer
  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>

      {/* Drawer panel */}
      <div className="relative ml-auto w-full max-w-md bg-white h-full shadow-xl overflow-y-auto transform transition-transform duration-300 ease-in-out">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-lg font-semibold">Save delivery address</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <FontAwesomeIcon icon={faTimes} className="text-gray-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          {/* Map container */}
          <div className="relative mb-4">
            <div className="h-48 rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapPosition}
                zoom={15}
                onClick={onMapClick}
                onLoad={(map) => {
                  mapRef.current = map;
                  console.log("✅ Google Map loaded");
                }}
                onUnmount={() => console.log("Google Map unmounted")}
                options={{
                  // Add any options if needed
                }}
              >
                <Marker
                  position={mapPosition}
                  draggable={true}
                  onDragEnd={onMarkerDragEnd}
                  onLoad={(marker) => {
                    markerRef.current = marker;
                  }}
                />
              </GoogleMap>
            </div>
            {/* Current location button */}
            <button
              type="button"
              onClick={handleUseCurrentLocation}
              className="absolute top-2 right-2 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 transition-colors"
              disabled={isLocating}
            >
              {isLocating ? (
                <FontAwesomeIcon icon={faSpinner} spin className="text-gray-500" />
              ) : (
                <FontAwesomeIcon icon={faCrosshairs} className="text-gray-700" />
              )}
            </button>
            {addressLoading && (
              <div className="absolute bottom-2 left-2 bg-white/80 p-1 px-2 rounded text-xs text-gray-600">
                Fetching address...
              </div>
            )}
          </div>

          {/* Address with Autocomplete */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <Autocomplete
              onLoad={(ref) => (autocompleteRef.current = ref)}
              onPlaceChanged={onPlaceChanged}
              restrictions={{ country: "in" }}
            >
              <input
                type="text"
                name="address"
                value={addressForm.address}
                onChange={onAddressChange}
                placeholder="Search for area or click on map"
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                required
              />
            </Autocomplete>
          </div>

          {/* Door / Flat & Area */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Door / Flat No.</label>
              <input
                type="text"
                name="apartment"
                value={addressForm.apartment}
                onChange={onAddressChange}
                placeholder="Flat / House no."
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
              <input
                type="text"
                name="area"
                value={addressForm.area}
                onChange={onAddressChange}
                placeholder="Area / Colony"
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          {/* Landmark */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Landmark</label>
            <input
              type="text"
              name="landmark"
              value={addressForm.landmark}
              onChange={onAddressChange}
              placeholder="Landmark (optional)"
              className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Address Type Buttons */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Address Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  onAddressChange({ target: { name: 'label', value: 15 } });
                  onAddressChange({ target: { name: 'label_name', value: 'Home' } });
                }}
                className={`flex-1 flex items-center justify-center gap-2 p-2 border rounded-lg ${
                  addressForm.label === 15 ? 'border-orange-500 bg-orange-50' : 'border-gray-300'
                }`}
              >
                <FontAwesomeIcon icon={faHome} className={addressForm.label === 15 ? 'text-orange-500' : 'text-gray-400'} />
                <span>Home</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  onAddressChange({ target: { name: 'label', value: 16 } });
                  onAddressChange({ target: { name: 'label_name', value: 'Work' } });
                }}
                className={`flex-1 flex items-center justify-center gap-2 p-2 border rounded-lg ${
                  addressForm.label === 16 ? 'border-orange-500 bg-orange-50' : 'border-gray-300'
                }`}
              >
                <FontAwesomeIcon icon={faBriefcase} className={addressForm.label === 16 ? 'text-orange-500' : 'text-gray-400'} />
                <span>Work</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  onAddressChange({ target: { name: 'label', value: 17 } });
                  onAddressChange({ target: { name: 'label_name', value: 'Other' } });
                }}
                className={`flex-1 flex items-center justify-center gap-2 p-2 border rounded-lg ${
                  addressForm.label === 17 ? 'border-orange-500 bg-orange-50' : 'border-gray-300'
                }`}
              >
                <FontAwesomeIcon icon={faMapMarkerAlt} className={addressForm.label === 17 ? 'text-orange-500' : 'text-gray-400'} />
                <span>Other</span>
              </button>
            </div>
            {addressForm.label === 17 && (
              <div className="mt-2">
                <input
                  type="text"
                  name="label_name"
                  value={addressForm.label_name}
                  onChange={onAddressChange}
                  placeholder="e.g., Dad’s home, my man cave"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : 'SAVE ADDRESS & PROCEED'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddressDrawer;